// src/pages/SignUp.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseclient";

export default function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const navigate = useNavigate();

  // Password validation function
  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return "Password must be at least 8 characters long";
    }
    // Check for at least one special character
    const specialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
    if (!specialCharRegex.test(pwd)) {
      return "Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)";
    }
    return "";
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordError(validatePassword(newPassword));
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    // Validate password before submitting
    const passwordValidationError = validatePassword(password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        setErrorMsg(error.message);
        setLoading(false);
        return;
      }

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session?.user) {
        console.error(
          "Could not fetch session or user:",
          sessionError?.message
        );
        setErrorMsg(
          "Account created but session error occurred. Please try signing in."
        );
        setLoading(false);
        return;
      }

      const user = session.user;

      // Add stub row to Users table
      try {
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Database insert timeout")), 5000);
        });

        const insertPromise = supabase.from("users").insert({
          id: user.id,
          email: user.email,
          profile_created: false,
        });

        const { error: insertError } = await Promise.race([
          insertPromise,
          timeoutPromise,
        ]);

        if (insertError) {
          console.error("Error saving profile:", insertError);
          setErrorMsg(
            "Account created, but profile failed to save. Please try signing in."
          );
          setLoading(false);
          return;
        }

        // ✅ Go to profile setup page
        navigate("/complete-profile");
      } catch (insertError) {
        console.error("Error inserting user record:", insertError);
        setErrorMsg(
          "Account created but profile setup failed. Please try signing in."
        );
        setLoading(false);
      }
    } catch (error) {
      console.error("Sign up error:", error);
      setErrorMsg("An unexpected error occurred during sign up.");
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: "400px", margin: "auto", paddingTop: "60px" }}>
      <h2>Sign Up</h2>
      <form onSubmit={handleSignUp}>
        <div style={{ marginBottom: "1rem" }}>
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "0.5rem",
              marginTop: "0.25rem",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={handlePasswordChange}
            required
            style={{
              width: "100%",
              padding: "0.5rem",
              marginTop: "0.25rem",
              border: passwordError
                ? "1px solid #dc3545"
                : password.length > 0 && !passwordError
                ? "1px solid #28a745"
                : "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
          {password.length > 0 && (
            <div style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
              <div
                style={{
                  color: password.length >= 8 ? "#28a745" : "#6c757d",
                  marginBottom: "0.25rem",
                }}
              >
                {password.length >= 8 ? "✓" : "○"} At least 8 characters
              </div>
              <div
                style={{
                  color: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
                    ? "#28a745"
                    : "#6c757d",
                }}
              >
                {/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
                  ? "✓"
                  : "○"}{" "}
                At least one special character
              </div>
            </div>
          )}
          {passwordError && (
            <p style={{ color: "#dc3545", fontSize: "0.85rem", marginTop: "0.5rem" }}>
              {passwordError}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || passwordError !== ""}
          style={{
            padding: "10px 20px",
            width: "100%",
            backgroundColor:
              loading || passwordError !== "" ? "#ccc" : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: loading || passwordError !== "" ? "not-allowed" : "pointer",
            opacity: loading || passwordError !== "" ? 0.7 : 1,
          }}
        >
          {loading ? "Creating Account..." : "Create Account"}
        </button>

        {errorMsg && <p style={{ color: "red" }}>{errorMsg}</p>}
      </form>

      {/* Sign In Link */}
      <div
        style={{
          textAlign: "center",
          marginTop: "2rem",
          paddingTop: "1rem",
          borderTop: "1px solid #eee",
        }}
      >
        <p>Already have an account?</p>
        <button
          onClick={() => navigate("/sign-in")}
          style={{
            background: "none",
            border: "none",
            color: "#007bff",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "1rem",
          }}
        >
          Sign in here
        </button>
      </div>
    </div>
  );
}
